import React, { useMemo } from 'react'
import ButtonGlobal from '@shared/Button'
import { SubmitHandler, useFormContext, useFormState } from 'react-hook-form'

import { OrderSalaryCardType, OrderType } from 'store/types/salaryCard'
import Dialog from '@shared/Dialog'
import { useState } from 'react'
import {
  useFetchUpdateOrderStatusMutation,
  useFetchUpdateSalaryCardOrderMutation,
} from 'store/reducers/salaryCards/salaryCardList.api'
import { AsanSigning } from '@shared/AsanSigning'
import { columns as defaultColumns } from '../../../../constants/salarycardform.columns'
import { useHistory } from 'react-router-dom'
import { ModalProvider } from '@shared/Modal'
import ResumeModalContent from 'pages/Orders/SalaryCardOrder/entities/ResumeModalContent/ResumeModalContent'
import { Col, Row, Space, Typography } from 'antd'
import { AlertIconIntoDetail } from '@modules/CustomIcons'
import { useTranslation } from 'react-i18next'
import { useFetchBranchListQuery } from 'store/reducers/configs/configs.api'

export function FieldUpdateActionsRight() {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState({})
  const { handleSubmit, trigger, getValues } = useFormContext()
  const { isValid } = useFormState()
  const { t } = useTranslation()
  const navigate = useHistory()

  const { data: branchList = [] } = useFetchBranchListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  const getBranchLabel = (value: string) =>
    branchList.find((item) => item.value === value)?.label || ''

  const [
    fetchUpdateSalaryCardOrder,
    { isLoading, isSuccess, data: orderData, error, status },
  ] = useFetchUpdateSalaryCardOrderMutation()

  const [
    updateOrderStatus,
    { status: upStatus, isSuccess: isUpSuccess, reset: resetUp },
  ] = useFetchUpdateOrderStatusMutation()

  const onSubmit: SubmitHandler<OrderSalaryCardType> = (data) => {
    setOpen(true)
    fetchUpdateSalaryCardOrder(data)
  }

  const columns = useMemo(
    () => ({ ...defaultColumns(), status: { width: 1, ellipsis: true } }),
    []
  )

  const resumeCompanyInfo_1 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.cardCurrency'),
      value: formData?.cardCurrency,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.cardType'),
      value: formData?.cardType,
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.orderTypes'),
      value: t(`orders.salaryCardOrder.constants.${formData?.orderType}`),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.receivingTransfer'),
      value: t(
        `orders.salaryCardOrder.constants.${formData?.orderDeliveryType}`
      ),
    },
  ]

  const resumeCompanyInfo_2 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.orderBranch'),
      value: getBranchLabel(formData?.orderBranch),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.BranchTransferred'),
      value: getBranchLabel(formData?.branchTransferred),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.totalNumberOfRejections'),
      value: `${formData?.tableList?.length}/0`,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.note'),
      value: formData?.note,
    },
  ]

  const resumeCompanyInfo_3 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.fullNameEtibar'),
      value: formData?.trustedPerson?.fullName,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.pin'),
      value: formData?.trustedPerson?.pin,
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.phoneNumber'),
      value: formData?.trustedPerson?.phone,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.expiryDate'),
      value: formData?.trustedPerson?.expiryDate,
    },
  ]

  return (
    <>
      <ButtonGlobal
        customType="default"
        color="#015CD1"
        onClick={() => navigate.push('/orders/salary-card')}
      >
        {t('orders.salaryCardOrder.buttonsLabels.close')}
      </ButtonGlobal>
      <ModalProvider
        title={t('orders.detailsOrder')}
        destroyOnClose
        width="calc(100vw - 20%)"
        modalContent={
          <ResumeModalContent
            columns={columns}
            tableData={formData?.tableList}
            dataSource={[
              resumeCompanyInfo_1,
              resumeCompanyInfo_2,
              resumeCompanyInfo_3,
            ]}
          />
        }
        footer={({ closeModal }) => (
          <Row justify="space-between" align="middle">
            <Col span={6}>
              {formData?.orderType === OrderType.URGENT && (
                <Row
                  style={{
                    width: '350px',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F6FA',
                    fontSize: '10px',
                    fontWeight: 500,
                    display: 'flex',
                  }}
                >
                  <div style={{ marginRight: '8px' }}>
                    <AlertIconIntoDetail />
                  </div>

                  <Typography.Text>
                    {t('orders.salaryCardOrder.constants.commissionFee')}
                  </Typography.Text>
                </Row>
              )}
            </Col>
            <Col>
              <Space>
                <ButtonGlobal
                  customType="default"
                  color="#015CD1"
                  onClick={() => closeModal()}
                >
                  {t('orders.salaryCardOrder.buttonsLabels.close')}
                </ButtonGlobal>
                <ButtonGlobal
                  loading={isLoading}
                  customType="primary"
                  disabled={!isValid}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('orders.salaryCardOrder.buttonsLabels.update')}
                </ButtonGlobal>
                <AsanSigning
                  docType="SCO"
                  asanKey="asanSignWithUpdate"
                  handleSigning={() => {}}
                  checkSignCallback={() => isValid || isLoading}
                  status={upStatus}
                  message={isUpSuccess ? t('orders.salaryCardOrder.restrictionTexts.asanSuccess') : undefined}
                  onComplete={(data) => {
                    updateOrderStatus({
                      orderNo: orderData.orderNo,
                      ...data,
                    })
                  }}
                  afterCloseFn={() => {
                    resetUp()
                    closeModal()
                    navigate.push('/orders/salary-card')
                  }}
                  beforeFromSigning={async () => {
                    const isValid = await trigger()

                    if (isValid) {
                      let requestData
                      await handleSubmit((data) => {
                        requestData = data
                      })()

                      return {
                        mutation: fetchUpdateSalaryCardOrder,
                        requestData,
                        fileNameKey: 'orderNo',
                        successMessage: `${t('orders.updateButAsanError')} `,
                      }
                    }
                  }}
                />
              </Space>

              <Dialog
                open={open}
                title={t('common.attention')}
                message={isSuccess ? t('orders.updateSuccess') : error?.data}
                modalType={status}
                afterClose={() => {
                  setOpen(false)
                  closeModal()
                  navigate.push('/orders/salary-card')
                }}
              />
            </Col>
          </Row>
        )}
        bodyStyle={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
      >
        {({ openModal, setStatus }) => (
          <ButtonGlobal
            loading={isLoading}
            customType="primary"
            disabled={!isValid}
            onClick={() => {
              openModal('')
              handleSubmit(data => setFormData(data))()
            }}
          >
            {t('newTransfer.nextButton')}
          </ButtonGlobal>
        )}
      </ModalProvider>
    </>
  )
}
